<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { Toaster } from '@/components/ui/sonner'
import {
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  computed,
  defineAsyncComponent,
  ref,
  watch
} from 'vue'
import { useDarkModeStore } from '@/stores/darkMode'
import AppNavbar from '@/components/customUi/Navbar/AppNavbar.vue'
import { recordRoute } from '@/feature/tutorialAgent/tutorialHistory'
import GlobalNavigationSearch from '@/components/customUi/Navbar/GlobalNavigationSearch.vue'
import TutorialAgentLauncher from '@/components/customUi/TutorialAgent/TutorialAgentLauncher.vue'
import AssistantQuickPanel from '@/components/customUi/TutorialAgent/AssistantQuickPanel.vue'
import TutorialAgentOverlay from '@/components/customUi/TutorialAgent/TutorialAgentOverlay.vue'
import { useAssistantPanelStore } from '@/stores/assistantPanelStore'
import { Routes } from '@/router/routes'
import Button from '@/components/ui/button/Button.vue'
import Dialog from '@/components/ui/dialog/Dialog.vue'
import DialogContent from '@/components/ui/dialog/DialogContent.vue'
import DialogDescription from '@/components/ui/dialog/DialogDescription.vue'
import DialogFooter from '@/components/ui/dialog/DialogFooter.vue'
import DialogHeader from '@/components/ui/dialog/DialogHeader.vue'
import DialogTitle from '@/components/ui/dialog/DialogTitle.vue'
import { ExternalLink, TriangleAlert } from 'lucide-vue-next'
import {
  EXTERNAL_NAVIGATION_REQUEST_EVENT,
  navigateToExternal,
  requestExternalNavigation,
  type ExternalNavigationRequestDetail
} from '@/feature/externalNavigation/externalNavigationGuard'
import { useTranslateKey } from '@/lib/utils'

const VueQueryDevtools = import.meta.env.DEV
  ? defineAsyncComponent(() => import('@tanstack/vue-query-devtools').then(m => m.VueQueryDevtools))
  : null

const route = useRoute()
const userStore = useUserStore()
const darkModeStore = useDarkModeStore()
const assistantPanelStore = useAssistantPanelStore()
const { translateKey } = useTranslateKey()

const loggedIn = computed(() => userStore.loggedIn)
const isUser = computed(() => userStore.isUser)
const showNavbar = computed(() => !route.meta.hideNavbar)
const isOnboardingRoute = computed(() => route.name === Routes.ONBOARDING)
const showFloatingGlobalSearch = computed(
  () => loggedIn.value && !isOnboardingRoute.value && (!isUser.value || !showNavbar.value)
)
const showFloatingTutorialLauncher = computed(
  () => loggedIn.value && isUser.value && userStore.isBetaAccess && !showNavbar.value
)
const showAssistantQuickPanel = computed(() => {
  return loggedIn.value && isUser.value && userStore.isBetaAccess && route.name !== Routes.ASSISTANT
})
const floatingActionsRight = computed(() => {
  return assistantPanelStore.isOpen ? `${assistantPanelStore.panelWidth + 16}px` : '16px'
})
const pendingExternalNavigation = ref<ExternalNavigationRequestDetail | null>(null)
const externalNavigationDialogOpen = computed({
  get: () => pendingExternalNavigation.value !== null,
  set: value => {
    if (!value) pendingExternalNavigation.value = null
  }
})
const externalNavigationDisplayUrl = computed(() => {
  const url = pendingExternalNavigation.value?.url
  if (!url) return ''

  try {
    const parsed = new URL(url)
    const pathname = parsed.pathname === '/' ? '' : parsed.pathname
    const compactPath = pathname.length > 42 ? `${pathname.slice(0, 39)}...` : pathname
    return `${parsed.hostname}${compactPath}`
  } catch {
    return url.length > 80 ? `${url.slice(0, 77)}...` : url
  }
})

const getAnchorElementFromEvent = (event: MouseEvent): HTMLAnchorElement | null => {
  if (!(event.target instanceof Element)) return null
  const anchor = event.target.closest('a[href]')
  if (!(anchor instanceof HTMLAnchorElement)) return null
  return anchor
}

const maybeGuardAnchorNavigation = (event: MouseEvent): void => {
  if (event.defaultPrevented) return
  const anchor = getAnchorElementFromEvent(event)
  if (!anchor) return

  const href = anchor.getAttribute('href')
  if (!href) return

  const guarded = requestExternalNavigation(href, {
    target: anchor.target || '_self'
  })

  if (!guarded) return
  event.preventDefault()
}

const handleDocumentClick = (event: MouseEvent): void => {
  if (event.button !== 0) return
  maybeGuardAnchorNavigation(event)
}

const handleDocumentAuxClick = (event: MouseEvent): void => {
  if (event.button !== 1) return
  maybeGuardAnchorNavigation(event)
}

const handleExternalNavigationRequest = (event: Event): void => {
  const customEvent = event as CustomEvent<ExternalNavigationRequestDetail>
  if (!customEvent.detail?.url) return
  pendingExternalNavigation.value = customEvent.detail
}

const externalNavigationEventListener = (event: Event): void => {
  handleExternalNavigationRequest(event)
}

const confirmExternalNavigation = (): void => {
  if (!pendingExternalNavigation.value) return
  const request = pendingExternalNavigation.value
  pendingExternalNavigation.value = null
  navigateToExternal(request)
}

onBeforeMount(() => {
  if (darkModeStore.darkMode) {
    darkModeStore.toggleDarkMode()
  }
})

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
  document.addEventListener('auxclick', handleDocumentAuxClick)
  window.addEventListener(EXTERNAL_NAVIGATION_REQUEST_EVENT, externalNavigationEventListener)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
  document.removeEventListener('auxclick', handleDocumentAuxClick)
  window.removeEventListener(EXTERNAL_NAVIGATION_REQUEST_EVENT, externalNavigationEventListener)
})

watch(
  () => route.path,
  nextRoute => {
    recordRoute(nextRoute)
  },
  { immediate: true }
)

watch(
  () => route.name,
  nextRouteName => {
    if (nextRouteName === Routes.ASSISTANT) {
      assistantPanelStore.isOpen = false
    }
  }
)
</script>

<template>
  <VueQueryDevtools v-if="VueQueryDevtools" />
  <Toaster position="top-center" offset="20px" expand />
  <div>
    <div
      v-if="showFloatingGlobalSearch"
      class="fixed top-4 z-50 flex items-center gap-2 transition-[right] duration-200"
      :style="{ right: floatingActionsRight }"
    >
      <GlobalNavigationSearch />
      <TutorialAgentLauncher v-if="showFloatingTutorialLauncher" />
    </div>
    <div v-if="loggedIn && isUser">
      <AppNavbar
        v-if="showNavbar"
        :tutorial-open="assistantPanelStore.isOpen"
        :tutorial-width="assistantPanelStore.panelWidth"
      />
      <main
        class="flex w-full flex-col"
        :class="showNavbar ? 'mt-12 h-[calc(100vh-3rem)]' : 'h-screen'"
      >
        <div class="flex h-full min-h-0 w-full">
          <div class="flex min-w-0 flex-1">
            <RouterView
              class="h-full w-full flex-1"
              :class="{ 'pt-4': showNavbar && $route.name !== 'tutorial' }"
            />
          </div>
        </div>
      </main>
      <AssistantQuickPanel
        v-if="showAssistantQuickPanel"
        v-model:open="assistantPanelStore.isOpen"
      />
      <TutorialAgentOverlay />
    </div>
    <div v-else>
      <RouterView />
    </div>
    <Dialog v-model:open="externalNavigationDialogOpen">
      <DialogContent class="sm:max-w-lg">
        <DialogHeader class="min-w-0">
          <DialogTitle class="flex items-center gap-2">
            <TriangleAlert class="size-5 text-amber-500" />
            {{ translateKey('externalNavigation.leaveTitle') }}
          </DialogTitle>
          <DialogDescription
            class="max-w-full text-sm break-words"
            :title="pendingExternalNavigation?.url || ''"
          >
            {{ translateKey('externalNavigation.openExternalDescription') }}
          </DialogDescription>
        </DialogHeader>

        <div class="border-border rounded-md border p-3">
          <p class="text-sm font-medium">
            {{
              externalNavigationDisplayUrl || translateKey('externalNavigation.fallbackDisplayUrl')
            }}
          </p>
          <p class="text-muted-foreground mt-1 text-xs break-all">
            {{ pendingExternalNavigation?.url || translateKey('externalNavigation.unknownUrl') }}
          </p>
        </div>

        <DialogFooter class="w-full flex-wrap gap-2 sm:justify-end">
          <Button variant="outline" @click="externalNavigationDialogOpen = false">
            {{ translateKey('cancel') }}
          </Button>
          <Button
            class="gap-2"
            :disabled="!pendingExternalNavigation?.url"
            @click="confirmExternalNavigation"
          >
            <ExternalLink class="size-4" />
            {{ translateKey('externalNavigation.visitExternalUrl') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
